import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";
import { Plus, Loader2 } from "lucide-react";

const SECTOR_UNITS = { SOLAR: "MWp", BUILDING: "sq.m", ROAD: "km", WATER: "km", TRANSMISSION: "MVA", INDUSTRIAL: "TPD", OTHER: "Nos" };
const SECTORS = [
  { value: "SOLAR", label: "Solar" }, { value: "BUILDING", label: "Building" }, { value: "ROAD", label: "Road / Highway" },
  { value: "WATER", label: "Water / Pipeline" }, { value: "TRANSMISSION", label: "Substation / Transmission" },
  { value: "INDUSTRIAL", label: "Industrial" }, { value: "OTHER", label: "Other" },
];

const EMPTY = {
  code: "", name: "", spv_name: "", location: "", company_id: "", sector: "SOLAR",
  dc_capacity_mwp: "", ac_capacity_mw: "", capacity_value: "", capacity_unit: "MWp",
  start_date: "", target_end_date: "", notes: "", wbs_template_id: "__default",
};

export function CreateProjectDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const { companies } = useCompany() || {};

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/wbs-templates", { params: { sector: form.sector } }).then(({ data }) => {
      setTemplates(data);
      const preferred = data.find((t) => t.sector === form.sector) || data[0];
      if (preferred) setForm((f) => ({ ...f, wbs_template_id: preferred.id }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.sector]);

  const setSector = (sector) => setForm({ ...form, sector, capacity_unit: SECTOR_UNITS[sector] });

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      const payload = {
        ...form,
        company_id: form.company_id || null,
        dc_capacity_mwp: form.sector === "SOLAR" && form.dc_capacity_mwp ? Number(form.dc_capacity_mwp) : null,
        ac_capacity_mw: form.sector === "SOLAR" && form.ac_capacity_mw ? Number(form.ac_capacity_mw) : null,
        capacity_value: form.capacity_value ? Number(form.capacity_value) : (form.sector === "SOLAR" ? Number(form.dc_capacity_mwp) || null : null),
        start_date: form.start_date || null,
        target_end_date: form.target_end_date || null,
        wbs_template_id: form.wbs_template_id && form.wbs_template_id !== "__default" ? form.wbs_template_id : null,
      };
      const { data } = await api.post("/projects", payload);
      toast.success(`Project ${data.code} created`);
      setForm(EMPTY);
      setOpen(false);
      onCreated?.(data);
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-project-button">
          <Plus className="h-4 w-4" /> New Project
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New EPC project</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-project-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Project code</Label>
              <Input required data-testid="project-code-input" value={form.code} onChange={set("code")} className="mt-1.5" placeholder="SPV-002" />
              <FieldError error={errors.code} />
            </div>
            <div>
              <Label>Status</Label>
              <Input disabled value="ACTIVE" className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label>Project name</Label>
            <Input required data-testid="project-name-input" value={form.name} onChange={set("name")} className="mt-1.5" />
            <FieldError error={errors.name} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>SPV name</Label>
              <Input data-testid="project-spv-input" value={form.spv_name} onChange={set("spv_name")} className="mt-1.5" />
            </div>
            <div>
              <Label>Location</Label>
              <Input data-testid="project-location-input" value={form.location} onChange={set("location")} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label>Company</Label>
            <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
              <SelectTrigger data-testid="project-company-select" className="mt-1.5"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                {(companies || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Sector</Label>
            <Select value={form.sector} onValueChange={setSector}>
              <SelectTrigger data-testid="project-sector-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{SECTORS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.sector === "SOLAR" ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>DC capacity (MWp)</Label>
                <Input type="number" step="0.01" data-testid="project-dc-capacity-input" value={form.dc_capacity_mwp} onChange={set("dc_capacity_mwp")} className="mt-1.5" />
              </div>
              <div>
                <Label>AC capacity (MW)</Label>
                <Input type="number" step="0.01" data-testid="project-ac-capacity-input" value={form.ac_capacity_mw} onChange={set("ac_capacity_mw")} className="mt-1.5" />
              </div>
            </div>
          ) : (
            <div>
              <Label>Capacity ({form.capacity_unit})</Label>
              <Input type="number" step="0.01" data-testid="project-capacity-value-input" value={form.capacity_value} onChange={set("capacity_value")} className="mt-1.5" placeholder={`e.g. 12 ${form.capacity_unit}`} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start date</Label>
              <Input type="date" data-testid="project-start-date-input" value={form.start_date} onChange={set("start_date")} className="mt-1.5" />
            </div>
            <div>
              <Label>Target end date</Label>
              <Input type="date" data-testid="project-target-end-date-input" value={form.target_end_date} onChange={set("target_end_date")} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label>WBS template <span className="font-normal text-muted-foreground">(builds Engineering, Procurement &amp; Construction automatically)</span></Label>
            <Select value={form.wbs_template_id || "none"} onValueChange={(v) => setForm({ ...form, wbs_template_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="project-template-select" className="mt-1.5"><SelectValue placeholder="Select template" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No template — I'll build the WBS myself</SelectItem>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.wbs_template_id && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                {(form.sector === "SOLAR" ? form.dc_capacity_mwp : form.capacity_value)
                  ? `Quantities per ${form.capacity_unit} will be scaled for ${form.sector === "SOLAR" ? form.dc_capacity_mwp : form.capacity_value} ${form.capacity_unit}.`
                  : `Enter the capacity so quantities per ${form.capacity_unit} can be calculated.`}
              </p>
            )}
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea data-testid="project-notes-input" value={form.notes} onChange={set("notes")} className="mt-1.5" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-project-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create project
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
